import { useCallback, useEffect, useRef, useState } from 'react';
import { useSSE } from '@/hooks/sse/useSSE';
import {
  getTalkFlowChatMessages,
  postTalkFlowChat,
  useTalkFlowChatSessions,
} from '@/hooks/apiHooks';
import { useSnackbar } from '@/hooks/global-tools';
import {
  TalkFlowChatMessage,
  TalkFlowChatSession,
  TalkFlowChatSourceScope,
} from '@/types/api';

// 화면용 메시지 모델 (서버 이력 + 낙관적 전송 항목 통합)
export type ChatUIMessage =
  | {
      kind: 'user';
      id: string;
      text: string;
      sourceScope: TalkFlowChatSourceScope;
      isAnimated?: boolean; // 방금 보낸 메시지만 true (이력은 애니메이션 없이 표시)
    }
  | {
      kind: 'assistant';
      id: string;
      aiPipelineId: string; // 이력 항목은 '' (매칭 불필요)
      text: string;
      status: 'pending' | 'done' | 'error';
      isAnimated?: boolean; // 방금 생성된 답변만 true
    }
  | { kind: 'source_changed'; id: string };

const HISTORY_LIMIT = 50;

// 답변 대기 복원 폴링 (PORTAL-13363): 답변 생성 중 모달을 닫으면 SSE 구독이 끊기고,
// 재진입 시엔 threadId(topic)를 알 수 없어 재구독이 불가 → 이력을 주기적으로 다시 읽어 답변을 받는다.
const PENDING_POLL_INTERVAL_MS = 3000;
const PENDING_POLL_TIMEOUT_MS = 3 * 60 * 1000;
const RESTORED_PENDING_ID = 'tmp-restored-pending';

// 이력의 마지막 항목이 질문이면 아직 답변이 오지 않은 상태
const endsWithUnanswered = (list: ChatUIMessage[]) =>
  list[list.length - 1]?.kind === 'user';

// 복원 대상 = 가장 최근에 만들어진 세션.
// 목록 순서에 기대지 않고 createdAt 으로 직접 고른다 — 채팅 초기화로 세션이 늘어나므로
// 순서가 바뀌면 초기화 전 대화가 되살아난다.
const latestSessionId = (sessions: TalkFlowChatSession[]) =>
  [...sessions].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]?.id;

// 서버 이력(최신순) → 화면용(과거→최신) 변환
const mapHistory = (messages: TalkFlowChatMessage[]): ChatUIMessage[] =>
  [...messages].reverse().flatMap((m): ChatUIMessage[] => {
    if (m.type === 'source_changed') {
      return [{ kind: 'source_changed', id: m.id }];
    }
    if (m.role === 'user') {
      return [
        {
          kind: 'user',
          id: m.id,
          text: m.content ?? '',
          sourceScope: m.sourceScope ?? 'recording_and_content',
        },
      ];
    }
    if (m.role === 'assistant') {
      return [
        {
          kind: 'assistant',
          id: m.id,
          aiPipelineId: '',
          text: m.content ?? '',
          status: 'done',
        },
      ];
    }
    return [];
  });

/**
 * 회의록 AI 채팅 상태 훅 (PORTAL-11968).
 *
 * - 단일 연속 스레드: 진입 시 세션목록의 최신 세션 1개를 복원해 이력 로드.
 * - 전송: POST .../chat → 202 {sessionId, aiPipelineId} → 낙관적 말풍선.
 *   답변은 SSE(topic `talk-flow-chat:{sessionId}`, event `talk_flow.chat.answer.*`)로
 *   `aiPipelineId` 매칭해 채움. (BE는 완성 답변 1건만 발행 → 로딩→완성 렌더)
 */
const useTalkFlowChat = (docId: string) => {
  const { showError } = useSnackbar();
  const {
    data: sessions,
    error: sessionsError,
    mutate: mutateSessions,
  } = useTalkFlowChatSessions(docId);

  const [sessionId, setSessionId] = useState<string | null>(null);
  // 실시간 답변 구독 topic — POST 응답의 topic(=talk-flow-chat:{threadId}) 을 그대로 사용.
  // (sessionId 로 구성하면 안 됨: 실제 topic 은 threadId 기반)
  const [activeTopic, setActiveTopic] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatUIMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  // 최초 진입 로딩: 세션목록 조회 + (있으면) 첫 이력 로드가 끝날 때까지 true.
  // 이 값이 true 인 동안엔 스피너만 보여줘 "빈 인사말 → 스피너 → 메시지" 깜빡임을 막는다.
  const [isInitializing, setIsInitializing] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const idRef = useRef(0);
  const nextId = () => `tmp-${(idRef.current += 1)}`;
  const restoredRef = useRef(false);
  const pendingPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPendingPoll = useCallback(() => {
    if (pendingPollRef.current) {
      clearInterval(pendingPollRef.current);
      pendingPollRef.current = null;
    }
  }, []);

  // 재진입 시 답변 미도착 상태 복원: 대기 말풍선을 붙이고 이력을 폴링해 답변이 오면 교체
  const startPendingPoll = useCallback(
    (session: string) => {
      stopPendingPoll();
      setMessages((prev) => [
        ...prev,
        {
          kind: 'assistant',
          id: RESTORED_PENDING_ID,
          aiPipelineId: '',
          text: '',
          status: 'pending',
        },
      ]);
      setIsSending(true);

      const startedAt = Date.now();
      pendingPollRef.current = setInterval(async () => {
        try {
          const list = await getTalkFlowChatMessages(docId, session, {
            limit: HISTORY_LIMIT,
          });
          const mapped = mapHistory(list ?? []);
          if (endsWithUnanswered(mapped)) {
            if (Date.now() - startedAt < PENDING_POLL_TIMEOUT_MS) return;
            // 시간 초과 → 오류 말풍선으로 전환
            stopPendingPoll();
            setMessages((prev) =>
              prev.map((m) =>
                m.id === RESTORED_PENDING_ID && m.kind === 'assistant'
                  ? { ...m, status: 'error' as const }
                  : m,
              ),
            );
            setIsSending(false);
            return;
          }
          stopPendingPoll();
          const last = mapped[mapped.length - 1];
          setMessages(
            mapped.map((m) =>
              m === last && m.kind === 'assistant'
                ? { ...m, isAnimated: true }
                : m,
            ),
          );
          setHasMore((list?.length ?? 0) >= HISTORY_LIMIT);
          setIsSending(false);
        } catch {
          // 무시 — 다음 주기에 재시도
        }
      }, PENDING_POLL_INTERVAL_MS);
    },
    [docId, stopPendingPoll],
  );

  useEffect(() => stopPendingPoll, [stopPendingPoll]);

  // docId 변경 시 초기화
  useEffect(() => {
    stopPendingPoll();
    restoredRef.current = false;
    setSessionId(null);
    setActiveTopic(null);
    setMessages([]);
    setIsSending(false);
    setIsInitializing(true);
    setHasMore(false);
    setIsLoadingMore(false);
  }, [docId, stopPendingPoll]);

  // 최초 진입: 최신 세션 복원 + 이력 로드 (한 번만)
  useEffect(() => {
    if (restoredRef.current) return;
    // 세션목록 조회 실패 → 새 세션으로 시작(로딩 종료)
    if (sessionsError) {
      restoredRef.current = true;
      setIsInitializing(false);
      return;
    }
    if (!sessions) return; // 아직 세션목록 로딩 중 → 스피너 유지

    restoredRef.current = true;

    const latest = latestSessionId(sessions);
    if (!latest) {
      // 이전 대화 없음 → 새 세션으로 시작(빈 인사말 표시)
      setIsInitializing(false);
      return;
    }

    setSessionId(latest);
    setIsLoadingHistory(true);
    getTalkFlowChatMessages(docId, latest, { limit: HISTORY_LIMIT })
      .then((list) => {
        const mapped = mapHistory(list ?? []);
        setMessages(mapped);
        setHasMore((list?.length ?? 0) >= HISTORY_LIMIT);
        // 답변 생성 중 모달을 닫았다 다시 연 경우 → 대기 말풍선 + 폴링으로 답변 수신
        if (endsWithUnanswered(mapped)) startPendingPoll(latest);
      })
      .catch(() => undefined)
      .finally(() => {
        setIsLoadingHistory(false);
        setIsInitializing(false);
      });
  }, [sessions, sessionsError, docId, startPendingPoll]);

  // 위로 스크롤 시 과거 메시지 로드 (beforeId 페이지네이션)
  const loadMore = useCallback(async () => {
    if (!sessionId || isLoadingMore || !hasMore) return;
    // 가장 오래된 서버 메시지 id 를 beforeId 로 사용 (낙관적 tmp- 항목 제외)
    const oldest = messages.find((m) => !m.id.startsWith('tmp-'));
    if (!oldest) return;

    setIsLoadingMore(true);
    try {
      const older = await getTalkFlowChatMessages(docId, sessionId, {
        limit: HISTORY_LIMIT,
        beforeId: oldest.id,
      });
      setMessages((prev) => [...mapHistory(older ?? []), ...prev]);
      setHasMore((older?.length ?? 0) >= HISTORY_LIMIT);
    } catch {
      // 무시 — 다음 스크롤에서 재시도
    } finally {
      setIsLoadingMore(false);
    }
  }, [sessionId, docId, isLoadingMore, hasMore, messages]);

  // 실시간 답변 수신 — 세션당 1회 구독
  const handlers = {
    'talk_flow.chat.answer.complete': (data: any) => {
      const aiPipelineId = data?.payload?.aiPipelineId;
      if (!aiPipelineId) return;
      setMessages((prev) =>
        prev.map((m) =>
          m.kind === 'assistant' && m.aiPipelineId === aiPipelineId
            ? { ...m, text: data.payload.answer ?? '', status: 'done' as const }
            : m,
        ),
      );
      setIsSending(false);
    },
    'talk_flow.chat.answer.error': (data: any) => {
      const aiPipelineId = data?.payload?.aiPipelineId;
      if (!aiPipelineId) return;
      setMessages((prev) =>
        prev.map((m) =>
          m.kind === 'assistant' && m.aiPipelineId === aiPipelineId
            ? { ...m, status: 'error' as const }
            : m,
        ),
      );
      setIsSending(false);
    },
  };

  useSSE(activeTopic ? [activeTopic] : [], handlers, !!activeTopic);

  const send = useCallback(
    async (rawText: string, scope: TalkFlowChatSourceScope) => {
      const text = rawText.trim();
      if (!text || isSending) return;

      const assistantId = nextId();
      setMessages((prev) => [
        ...prev,
        {
          kind: 'user',
          id: nextId(),
          text,
          sourceScope: scope,
          isAnimated: true,
        },
        {
          kind: 'assistant',
          id: assistantId,
          aiPipelineId: '',
          text: '',
          status: 'pending',
          isAnimated: true,
        },
      ]);
      setIsSending(true);

      try {
        const { data } = await postTalkFlowChat(docId, {
          message: text,
          sourceScope: scope,
          sessionId,
        });
        if (!sessionId) {
          setSessionId(data.sessionId);
          // 첫 채팅으로 세션이 새로 생성됨 → 세션목록 캐시를 갱신해 둔다.
          // (안 하면 채팅창을 닫았다 다시 열 때 stale 한 [] 를 읽어
          //  방금 만든 세션을 못 잡고 또 새 세션으로 시작해 이력이 분리된다)
          mutateSessions();
        }
        // 답변 구독 topic 은 응답의 topic 을 그대로 사용(threadId 기반). 매 전송마다 갱신.
        setActiveTopic(data.topic);
        // 응답으로 받은 aiPipelineId 를 pending 말풍선에 연결(SSE 매칭 기준)
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId && m.kind === 'assistant'
              ? { ...m, aiPipelineId: data.aiPipelineId }
              : m,
          ),
        );
      } catch (e) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId && m.kind === 'assistant'
              ? { ...m, status: 'error' as const }
              : m,
          ),
        );
        setIsSending(false);
        showError({ message: '메시지 전송에 실패했습니다.' });
      }
    },
    [docId, sessionId, isSending, mutateSessions, showError],
  );

  // 진행 중 답변 중단 — BE 취소 API 가 없어 로컬에서만 대기 해제 + pending 말풍선 제거.
  // (이후 SSE 로 답변이 오더라도 매칭할 pending 말풍선이 없어 무시됨)
  const stop = useCallback(() => {
    if (!isSending) return;
    stopPendingPoll();
    setMessages((prev) =>
      prev.filter((m) => !(m.kind === 'assistant' && m.status === 'pending')),
    );
    setIsSending(false);
  }, [isSending, stopPendingPoll]);

  // 채팅 초기화 — 세션 연결을 끊고 화면을 비운다. 별도 API 없이,
  // 다음 send 가 sessionId 없이 POST 하면 서버가 새 세션을 발급한다(= 새 스레드 시작).
  const resetChat = useCallback(() => {
    stopPendingPoll();
    setSessionId(null);
    setActiveTopic(null);
    setMessages([]);
    setIsSending(false);
    setHasMore(false);
    setIsLoadingMore(false);
  }, [stopPendingPoll]);

  return {
    messages,
    isSending,
    isLoadingHistory,
    isInitializing,
    send,
    stop,
    resetChat,
    loadMore,
    hasMore,
    isLoadingMore,
  };
};

export default useTalkFlowChat;
